import { useEffect, useState } from 'react';
import { subscribeAdmins, subscribeAllInvites, subscribeAllPartnerUsers } from '@/data/accountsRepo';
import type { AdminUserType } from '@/types/types_admin';
import type { InviteType } from '@/types/types_invite';
import type { PartnerUserType } from '@/types/types_partnerUser';

// Roster-wide subscriptions for the admin accounts page. Same shape as
// useInvites.ts — subscribe on mount, set state only from the snapshot
// callback — but unscoped, so they are admin-only reads.

/** Every partner account across all partners. */
export function useAllPartnerUsers() {
  const [partnerUsers, setPartnerUsers] = useState<PartnerUserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeAllPartnerUsers(
      (list) => {
        setPartnerUsers(list);
        setLoading(false);
        setError(null);
      },
      (e) => {
        console.error('Partner user roster subscription failed', e);
        setError('Could not load partner accounts.');
        setLoading(false);
      },
    );
  }, []);

  return { partnerUsers, loading, error };
}

/** Every invitation across all partners, newest first. */
export function useAllInvites() {
  const [invites, setInvites] = useState<InviteType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeAllInvites(
      (list) => {
        setInvites(list);
        setLoading(false);
        setError(null);
      },
      (e) => {
        console.error('Invite roster subscription failed', e);
        setError('Could not load invitations.');
        setLoading(false);
      },
    );
  }, []);

  return { invites, loading, error };
}

/** The Eighty Days admin roster. */
export function useAdmins() {
  const [admins, setAdmins] = useState<AdminUserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeAdmins(
      (list) => {
        setAdmins(list);
        setLoading(false);
        setError(null);
      },
      (e) => {
        console.error('Admin roster subscription failed', e);
        setError('Could not load administrators.');
        setLoading(false);
      },
    );
  }, []);

  return { admins, loading, error };
}
